/**
 * 视频换脸节点 - Video Face Swap Pro
 * 输入：目标人脸图片 + 源视频
 * 输出：换脸后的视频任务节点
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './VideoFaceSwapNode.css';

const apiService = new ApiService();

export const VideoFaceSwapNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedFaceImage, setConnectedFaceImage] = useState(null);
  const [connectedSourceVideo, setConnectedSourceVideo] = useState(null);

  // 获取连接到此节点的目标人脸图片（face输入点）
  const getConnectedFaceImage = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'face'
    );
    
    console.log(`VideoFaceSwapNode ${id} - 连接到人脸输入的边:`, connectedEdges.length);
    
    let imageUrl = null;
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`VideoFaceSwapNode ${id} - 人脸源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrl = url;
          console.log(`VideoFaceSwapNode ${id} - 从图片参考节点获取人脸:`, url);
        }
      }
      
      // 从图片展示节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrl = url;
          console.log(`VideoFaceSwapNode ${id} - 从图片展示节点获取人脸:`, url);
        }
      }
      
      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrl = url;
          console.log(`VideoFaceSwapNode ${id} - 从手绘节点获取人脸:`, url);
        }
      }
      
      // 从图片任务节点获取生成的图片（取第一张）
      if (sourceNode && sourceNode.type === 'imageTask') {
        if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
          const firstImage = sourceNode.data.generatedImages[0];
          const url = typeof firstImage === 'string' ? firstImage : firstImage.url;
          if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrl = url;
            console.log(`VideoFaceSwapNode ${id} - 从任务节点获取人脸:`, url);
          }
        }
      }
    });
    
    return imageUrl;
  }, [edges, nodes, getNode, id]);

  // 获取连接到此节点的源视频（video输入点）
  const getConnectedSourceVideo = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'video'
    );
    
    console.log(`VideoFaceSwapNode ${id} - 连接到视频输入的边:`, connectedEdges.length);
    
    let videoUrl = null;
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`VideoFaceSwapNode ${id} - 视频源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从视频参考节点获取视频
      if (sourceNode && sourceNode.type === 'videoReference' && sourceNode.data?.videoUrl) {
        const url = sourceNode.data.videoUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          videoUrl = url;
          console.log(`VideoFaceSwapNode ${id} - 从视频参考节点获取源视频:`, url);
        }
      }
      
      // 从视频任务节点获取生成的视频
      if (sourceNode && sourceNode.type === 'videoTask') {
        if (sourceNode.data?.videoUrl) {
          const url = sourceNode.data.videoUrl;
          if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            videoUrl = url;
            console.log(`VideoFaceSwapNode ${id} - 从视频任务节点获取源视频:`, url);
          }
        }
      }
    });
    
    return videoUrl;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片和视频
  useEffect(() => {
    const faceImage = getConnectedFaceImage();
    const sourceVideo = getConnectedSourceVideo();
    setConnectedFaceImage(faceImage);
    setConnectedSourceVideo(sourceVideo);
  }, [getConnectedFaceImage, getConnectedSourceVideo]);

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 验证必需参数
      if (!connectedFaceImage) {
        setError('请先连接目标人脸图片节点');
        toast.error('请先连接目标人脸图片节点');
        setIsGenerating(false);
        return;
      }
      
      if (!connectedSourceVideo) {
        setError('请先连接源视频节点');
        toast.error('请先连接源视频节点');
        setIsGenerating(false);
        return;
      }
      
      console.log('使用目标人脸:', connectedFaceImage);
      console.log('使用源视频:', connectedSourceVideo);
      
      // 2. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 3. 构建请求参数
      const inputParams = {
        face_url: connectedFaceImage,
        source: connectedSourceVideo
      };
      
      console.log('请求参数:', {
        app_name: 'video-face-swap-pro',
        task_type: 'video_face_swap_api',
        input_params: inputParams
      });
      
      // 4. 调用 V2 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/v2/tasks/create`,
        'POST',
        {
          token: userData.token,
          app_name: 'video-face-swap-pro',
          input_params: inputParams,
          task_type: 'video_face_swap_api'
        }
      );
      
      console.log('任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        
        toast.success('视频换脸任务已创建！');
        
        // 5. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        // 6. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: '视频换脸',
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 7. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建视频换脸任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('视频换脸失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    console.log('VideoFaceSwapNode - 验证连接:', connection);
    
    // 如果连接到人脸输入handle
    if (connection.targetHandle === 'face') {
      const sourceNode = getNode(connection.source);
      console.log('VideoFaceSwapNode - 人脸连接验证, 源节点:', sourceNode?.type);
      
      const validImageTypes = ['imageReference', 'imageDisplay', 'drawing', 'imageTask'];
      
      if (!sourceNode || !validImageTypes.includes(sourceNode.type)) {
        console.log('VideoFaceSwapNode - 人脸节点类型验证失败');
        toast.warning('请连接图片节点作为目标人脸');
        return false;
      }
      
      // 检查当前已连接的人脸图片数量（只允许1个）
      const currentEdges = getEdges();
      const faceEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'face'
      ).length;
      
      if (faceEdgesCount >= 1) {
        toast.warning('目标人脸只能连接一个图片节点');
        return false;
      }
      
      console.log('VideoFaceSwapNode - 人脸连接验证通过');
      return true;
    }
    
    // 如果连接到视频输入handle
    if (connection.targetHandle === 'video') {
      const sourceNode = getNode(connection.source);
      console.log('VideoFaceSwapNode - 视频连接验证, 源节点:', sourceNode?.type);
      
      const validVideoTypes = ['videoReference', 'videoTask'];
      
      if (!sourceNode || !validVideoTypes.includes(sourceNode.type)) {
        console.log('VideoFaceSwapNode - 视频节点类型验证失败');
        toast.warning('请连接视频节点作为源视频');
        return false;
      }
      
      // 检查当前已连接的源视频数量（只允许1个）
      const currentEdges = getEdges();
      const videoEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'video'
      ).length;
      
      if (videoEdgesCount >= 1) {
        toast.warning('源视频只能连接一个视频节点');
        return false;
      }
      
      console.log('VideoFaceSwapNode - 视频连接验证通过');
      return true;
    }
    
    return true;
  }, [getNode, getEdges, id]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'videoFaceSwap', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="VideoFaceSwapNode-container">
      {/* 人脸输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="face"
        className="VideoFaceSwapNode-handle VideoFaceSwapNode-handle-face"
        isValidConnection={isValidConnection}
        style={{ top: '45%' }}
      >
        <div 
          className="VideoFaceSwapNode-handle-label VideoFaceSwapNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'face', 'image')}
          title="点击自动创建图片参考节点"
        >
          人脸
        </div>
      </Handle>

      {/* 视频输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="video"
        className="VideoFaceSwapNode-handle VideoFaceSwapNode-handle-video"
        isValidConnection={isValidConnection}
        style={{ top: '55%' }}
      >
        <div 
          className="VideoFaceSwapNode-handle-label VideoFaceSwapNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'video', 'video')}
          title="点击自动创建视频参考节点"
        >
          视频
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="VideoFaceSwapNode-handle VideoFaceSwapNode-handle-output"
      />

      {/* 头部信息 */}
      <div className="VideoFaceSwapNode-header">
        <div className="VideoFaceSwapNode-header-left">
          <div className="VideoFaceSwapNode-icon">🎭</div>
          <div className="VideoFaceSwapNode-header-text">
            <div className="VideoFaceSwapNode-title">视频换脸</div>
            <div className="VideoFaceSwapNode-subtitle">Video Face Swap Pro</div>
          </div>
        </div>
        <button 
          className="VideoFaceSwapNode-duplicate-button"
          onClick={handleDuplicate}
          title="复制节点"
        >
          <i className="bi bi-copy"></i>
        </button>
      </div>

      {/* 内容区域 */}
      <div className="VideoFaceSwapNode-content">
        <div className="VideoFaceSwapNode-media-grid">
          <div className={`VideoFaceSwapNode-media-card VideoFaceSwapNode-media-card-face ${connectedFaceImage ? 'VideoFaceSwapNode-media-card-active' : ''}`}>
            <div className="VideoFaceSwapNode-card-header">
              <span className="VideoFaceSwapNode-dot VideoFaceSwapNode-dot-face" />
              <span className="VideoFaceSwapNode-card-title">目标人脸</span>
              {connectedFaceImage && <span className="VideoFaceSwapNode-badge">已连接</span>}
            </div>
            <div className="VideoFaceSwapNode-card-body">
              {connectedFaceImage ? (
                <div className="VideoFaceSwapNode-image-preview nodrag">
                  <img 
                    src={connectedFaceImage} 
                    alt="目标人脸"
                    className="VideoFaceSwapNode-preview-img"
                  />
                </div>
              ) : (
                <div className="VideoFaceSwapNode-empty">
                  <span className="VideoFaceSwapNode-empty-icon">🖼️</span>
                  <span>连接图片节点添加目标人脸</span>
                </div>
              )}
            </div>
          </div>

          <div className={`VideoFaceSwapNode-media-card VideoFaceSwapNode-media-card-video ${connectedSourceVideo ? 'VideoFaceSwapNode-media-card-active' : ''}`}>
            <div className="VideoFaceSwapNode-card-header">
              <span className="VideoFaceSwapNode-dot VideoFaceSwapNode-dot-video" />
              <span className="VideoFaceSwapNode-card-title">源视频</span>
              {connectedSourceVideo && <span className="VideoFaceSwapNode-badge">已连接</span>}
            </div>
            <div className="VideoFaceSwapNode-card-body">
              {connectedSourceVideo ? (
                <div className="VideoFaceSwapNode-video-preview nodrag">
                  <video 
                    src={connectedSourceVideo} 
                    className="VideoFaceSwapNode-preview-video"
                    controls
                  >
                    您的浏览器不支持视频播放
                  </video>
                </div>
              ) : (
                <div className="VideoFaceSwapNode-empty">
                  <span className="VideoFaceSwapNode-empty-icon">🎞️</span>
                  <span>连接视频节点添加源视频</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="VideoFaceSwapNode-info">
          <div className="VideoFaceSwapNode-hint">
            <div className="VideoFaceSwapNode-hint-title">
              <span className="VideoFaceSwapNode-hint-icon">💡</span>
              使用说明
            </div>
            <ul className="VideoFaceSwapNode-hint-list">
              <li><span>目标人脸</span>：将被替换到视频中的人脸图片</li>
              <li><span>源视频</span>：需要进行人脸替换的视频素材</li>
              <li>图片和视频都需要提供可访问的 URL</li>
              <li>创建成功后会自动生成任务节点追踪进度</li>
            </ul>
          </div>
          <div className="VideoFaceSwapNode-actions">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || readOnly}
              className={`VideoFaceSwapNode-generate ${isGenerating || readOnly ? 'VideoFaceSwapNode-generate-disabled' : ''}`}
            >
              {isGenerating ? '创建中...' : '创建任务'}
            </button>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="VideoFaceSwapNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

VideoFaceSwapNode.displayName = 'VideoFaceSwapNode';

